
import { useState } from "react";
import { Calculator, Shield, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useNavigate } from "react-router-dom";

export const CollateralCalculator = () => {
  const [propertyValue, setPropertyValue] = useState<number>(0);
  const [loanAmount, setLoanAmount] = useState<number>(0);
  const navigate = useNavigate();

  const collateralRatio = propertyValue > 0 ? (loanAmount / propertyValue) * 100 : 0;
  const isValidRatio = collateralRatio <= 70;
  const maxLoanAmount = propertyValue * 0.7;

  return (
    <Card className="border-2 border-primary/10 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-blue-50">
      <CardHeader className="bg-gradient-to-r from-primary/10 to-transparent border-b border-primary/5">
        <CardTitle className="flex items-center gap-2 text-primary">
          <Calculator className="h-5 w-5" />
          Collateral Value Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 p-5">
        <div className="space-y-2">
          <Label htmlFor="propertyValue" className="text-sm font-medium text-slate-700">Property Value (GHS)</Label>
          <Input
            id="propertyValue"
            type="number"
            min="0"
            value={propertyValue || ''}
            onChange={(e) => setPropertyValue(Number(e.target.value))}
            className="bg-white border-slate-200 focus:border-primary focus:ring-primary/30"
            placeholder="Enter property value"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="loanAmount" className="text-sm font-medium text-slate-700">Desired Loan Amount (GHS)</Label>
          <Input
            id="loanAmount"
            type="number"
            min="0"
            value={loanAmount || ''}
            onChange={(e) => setLoanAmount(Number(e.target.value))}
            className="bg-white border-slate-200 focus:border-primary focus:ring-primary/30"
            placeholder="Enter loan amount"
          />
        </div>
        
        {propertyValue > 0 && (
          <div className="space-y-3 pt-2">
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-700 font-medium">Collateral Ratio</span>
              <span className={`font-bold ${isValidRatio ? 'text-green-600' : 'text-red-600'}`}>
                {collateralRatio.toFixed(1)}%
              </span>
            </div>
            <Progress 
              value={Math.min(collateralRatio, 100)} 
              className="h-2"
              indicatorClassName={isValidRatio ? 'bg-green-500' : 'bg-red-500'}
            />
            <div className="flex justify-between text-xs text-slate-500">
              <span>0%</span>
              <span className="text-primary font-medium">70% Max</span>
              <span>100%</span>
            </div>
          </div>
        )}
        
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <p className="text-sm font-medium text-slate-800">Your Maximum Loan Eligibility</p>
              <p className="text-xl font-bold text-primary mt-1">
                {formatCurrency(maxLoanAmount, "GHS")}
              </p>
              {!isValidRatio && loanAmount > 0 && (
                <p className="text-xs text-red-600 mt-1 font-medium">
                  Your requested amount exceeds the maximum allowed
                </p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="p-5 pt-0">
        <Button 
          className="w-full flex items-center justify-center gap-2" 
          variant="default"
          onClick={() => navigate('/borrower/pre-approval')}
        >
          Apply for Pre-Approval <ArrowRight className="h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
};

// Helper function to format currency
const formatCurrency = (amount: number, currency = "GHS") => {
  return new Intl.NumberFormat("en-GH", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(amount);
};
